import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useDialog } from '../../hooks/useDialog'
import { usePagination } from '../../hooks/usePagination'
import catalogService from '../../services/catalogService'
import { Allergen, AllergenPayload } from '../../types/catalog'
import { Button } from '../../components/ui/Button'
import { Dialog } from '../../components/ui/Dialog'
import { Pagination } from '../../components/ui/Pagination'
import { AllergenForm } from './AllergenForm'
import {
  PageHeader,
  PageMeta,
  PageEyebrow,
  PageTitle,
  TableWrapper,
  StyledTable,
  THead,
  TH,
  EmptyState,
  TableSkeleton,
} from '../../components/ui/Table'
import { useConfirm } from '../../hooks/useConfirm'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { AllergenRow } from './AllergenRow'

export function AllergensPage() {
  const { token } = useAuth()
  const { page, perPage, setPage } = usePagination()
  const dialog = useDialog<Allergen>()

  const [items, setItems] = useState<Allergen[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [payload, setPayload] = useState<AllergenPayload | null>(null)

  const svc = useMemo(() => catalogService(token), [token])
  const { allergens } = svc

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await allergens.list(page, perPage)
      setItems(res.items)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [page, perPage, allergens])

  useEffect(() => {
    load()
  }, [load])

  const handleSave = useCallback(async () => {
    if (!payload) return
    setSaving(true)
    try {
      if (dialog.data) {
        await svc.allergens.update(dialog.data.id, payload)
      } else {
        await svc.allergens.create(payload)
      }
      dialog.close()
      load()
    } finally {
      setSaving(false)
    }
  }, [dialog, payload, svc, load])

  const confirmDialog = useConfirm()

  const handleDelete = useCallback(
    (item: Allergen) => {
      confirmDialog.confirm(
        'Delete allergen',
        `Are you sure you want to delete "${item.translations.find(t => t.locale === 'en')?.name ?? item.code}"? This action cannot be undone.`,
        async () => {
          await svc.allergens.delete(item.id)
          load()
        }
      )
    },
    [confirmDialog, svc, load]
  )

  return (
    <>
      <PageHeader>
        <PageMeta>
          <PageEyebrow>Catalog</PageEyebrow>
          <PageTitle>Allergens</PageTitle>
        </PageMeta>
        <Button onClick={() => dialog.open()}>+ Add allergen</Button>
      </PageHeader>

      {loading ? (
        <TableSkeleton cols={3} />
      ) : (
        <>
          <TableWrapper>
            <StyledTable>
              <THead>
                <tr>
                  <TH>Code</TH>
                  <TH>English</TH>
                  <TH>Spanish</TH>
                  <TH />
                </tr>
              </THead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={4}>
                      <EmptyState message="No allergens yet. Add one above." />
                    </td>
                  </tr>
                )}
                {items.map(item => (
                  <AllergenRow
                    key={item.id}
                    item={item}
                    onEdit={dialog.open}
                    onDelete={handleDelete}
                  />
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>
          <Pagination page={page} total={total} perPage={perPage} onChange={setPage} />
        </>
      )}

      <Dialog
        open={dialog.isOpen}
        title={dialog.data ? 'Edit allergen' : 'Add allergen'}
        onClose={dialog.close}
        onConfirm={handleSave}
        confirmLabel={dialog.data ? 'Save changes' : 'Add allergen'}
        confirmLoading={saving}
        confirmDisabled={!payload?.code?.trim()}
      >
        <AllergenForm initial={dialog.data} onChange={setPayload} />
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.state.open}
        title={confirmDialog.state.title}
        message={confirmDialog.state.message}
        confirmLabel="Delete"
        onConfirm={confirmDialog.state.onConfirm}
        onCancel={confirmDialog.cancel}
        loading={confirmDialog.loading}
        variant="danger"
      />
    </>
  )
}
